import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function SupplyChainExample({ logo }) {
  useSEO({
    title: 'Supply Chain Simulation',
    description: 'Simulate inventory flows, production schedules, and demand variability. Stop stockouts and overproduction before they happen.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      {/* Hero Section - Pain-focused, outcome-driven */}
      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Inventory + Production Optimization</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">
            Stop stockouts and overproduction before they happen
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Simulate inventory flows, production schedules, and demand variability to find the right balance—before costly mistakes hit your bottom line.
          </p>
        </div>

        {/* Who is this for */}
        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="font-medium text-gray-900 mb-2">Supply Chain Leaders</p>
              <p className="text-sm text-gray-600">Balancing service levels with inventory costs</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="font-medium text-gray-900 mb-2">Operations Executives</p>
              <p className="text-sm text-gray-600">Planning capacity amid demand uncertainty</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <p className="font-medium text-gray-900 mb-2">Manufacturing Planners</p>
              <p className="text-sm text-gray-600">Optimizing production schedules and changeovers</p>
            </div>
          </div>
        </div>

        {/* What decisions it improves */}
        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">How much safety stock to hold at each location</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">When to trigger production runs and reorders</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">Which suppliers to prioritize during disruptions</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">How demand variability affects capacity needs</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">Trade-offs between service levels and carrying costs</p>
              </div>
              <div className="flex items-start">
                <span className="text-gray-400 mr-3 mt-1">—</span>
                <p className="text-gray-700">Impact of lead time changes on the entire system</p>
              </div>
            </div>
          </div>
        </div>

        {/* Industry Example */}
        <div className="mb-16">
          <div className="bg-gray-900 text-white rounded-lg p-8 md:p-10">
            <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Example: Consumer Goods Manufacturing</p>
            <p className="text-lg leading-relaxed mb-6">
              A consumer goods manufacturer simulated raw material lead times, production capacity constraints, and seasonal demand patterns. 
              The model revealed that a modest increase in safety stock at one distribution center—combined with earlier production triggers—could 
              reduce stockouts by 40% while actually lowering total carrying costs.
            </p>
            <p className="text-gray-400 text-sm">
              The impact: $2.3M annual savings from reduced expediting costs and lost sales.
            </p>
          </div>
        </div>

                <HowToEngage
          platformDescription="Use our platform to model your supply chain, test scenarios, and share insights with your team. Ideal if you have analysts who want hands-on control."
          consultingDescription="Partner with our consultants to develop a custom model for your supply chain, run scenario workshops, and deliver board-ready recommendations."
        />

        {/* Related Solutions */}
        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/logistics-transportation" className="text-gray-700 hover:text-gray-900 text-sm">
              Route + Fleet Optimization →
            </Link>
            <Link to="/solutions/retail-ecommerce" className="text-gray-700 hover:text-gray-900 text-sm">
              Demand Forecasting + Pricing →
            </Link>
            <Link to="/solutions/mining-manufacturing" className="text-gray-700 hover:text-gray-900 text-sm">
              Asset Uptime + Maintenance →
            </Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default SupplyChainExample;
